<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/Projections_for_Expressions';
    let nextLink= '/Tutorial/More_Scoping';
    
    import Figure from '$lib/figures/Figure.svelte';
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="in-need-of-scoping-1"
  bind:intersecting={visibleById["in-need-of-scoping-1"]}
  onIntersectChange={(visible) => setVisible("in-need-of-scoping-1", visible)}
>In Need of Scoping</SectionComponent>
<p>In this lesson, we’ll restrict the question references used in a page’s grading to <strong>only</strong> the questions on that page.<br>
To do this, we need to define <strong>scope rules</strong>.</p>
<SectionComponent tag="h2" 
  id="a-simple-view-on-scoping-2"
  bind:intersecting={visibleById["a-simple-view-on-scoping-2"]}
  onIntersectChange={(visible) => setVisible("a-simple-view-on-scoping-2", visible)}
>A Simple View on Scoping</SectionComponent>
<p>Oh my, you might think—this is going to be difficult. But with Freon we stick to the philosophy:<br>
“<em>Simple things should be simple, complex things should be possible.</em>”
(See our <a href="/Background">Background</a> for more.)</p>
<p>Scoping answers the question: <strong>which names are visible where?</strong> In general, all names in a context are divided into <strong>sets</strong>.<br>
These sets don’t overlap, but there are subsets within larger sets, as shown below.</p>
<p>Let’s say the smallest set that contains a name <strong>owns</strong> that name. For example, in the figure the name “Peter” is owned by <code>set8</code>.<br>
Using common scoping rules, a name is visible in its owning set and in any set that <strong>contains</strong> the owning set, but not elsewhere.<br>
So “Peter” would be visible in (from smallest to largest) <code>set8</code>, <code>set4</code>, <code>set2</code>, and <code>set1</code>, but <strong>not</strong> in <code>set3</code>, <code>set5</code>, <code>set6</code>, or <code>set7</code>.</p>
<Figure
imageName={'tutorial/Tutorial-lesson6-namespaces.png'}
caption={'Simple View on Namespaces'}
figureNumber={1}
/>
<p>In Freon, these sets of names are called <strong>NameSpaces</strong>. Without any scope definitions, your editor has a <strong>single</strong> namespace containing every name in the model.</p>
<SectionComponent tag="h2" 
  id="defining-namespaces-3"
  bind:intersecting={visibleById["defining-namespaces-3"]}
  onIntersectChange={(visible) => setVisible("defining-namespaces-3", visible)}
>Defining namespaces</SectionComponent>
<p>There’s an easy way to define additional namespaces in Freon. Any file that defines scoping must use the <code>.scope</code> extension.<br>
Create a file called <code>edu.scope</code>.</p>
<p>First, think about the DSL: which names should be hidden in certain parts? Our problem is that <strong>question</strong> names are visible across <strong>all</strong> pages, not just the page where they’re defined.<br>
That suggests the <code>Page</code> concept should be a <strong>namespace</strong>. Add the following to <code>edu.scope</code>:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson6-defs/edu.scope

scoper for language Education

isNamespace &#123; Page  &#125;</code></pre>
<p>That’s it—easy peasy! Try it out: in a page’s <strong>Score</strong>, you can now only reference <strong>that page’s</strong> questions.</p>
<p>In the following lessons we’ll explore a few more scoping features. But first, we’ll define the last model unit: the <strong>Test</strong> unit.</p>


<PrevNextSection {prevLink} {nextLink} />